// Platform terminology — the single source of truth for the English
// defaults of every primitive noun the UI exposes. Per-tenant overrides
// (in tenant_settings.labels) layer on top via useLabel().
//
// Adding a new noun:
//   1. Add the key to `LabelKey` below.
//   2. Add the default in `DEFAULT_LABELS`.
//   3. Add it to the backend's `AllowedLabelKeys` map in
//      models/tenant_settings.go so admins can override it.
//   4. Use the key via <Label k="..." /> or useLabel().
//
// Don't reach for these labels from server-side renderers (PDFs, emails)
// without also wiring the server to read tenant_settings.labels — there
// is no shared resolver yet.

export type LabelKey =
  | 'site'
  | 'asset'
  | 'asset_type'
  | 'form'
  | 'submission'
  | 'schedule'
  | 'job_workflow'
  | 'maintenance';

export type LabelForms = {
  singular: string;
  plural: string;
};

// Defaults — what every fresh tenant sees until they customise.
// Title case. Verbs are derived from these via the helper templates
// below ("Add {singular}" etc.).
//
// `site` defaults to "Location" rather than "Site" to preserve the
// pre-overrides UX (which spelt the noun "Location" / "Locations"
// everywhere — sidebar, page headings, empty states). Internal code
// and DB schema still say `site` — that's the platform key, not the
// user-facing word.
export const DEFAULT_LABELS: Record<LabelKey, LabelForms> = {
  site:         { singular: 'Location',     plural: 'Locations' },
  asset:        { singular: 'Asset',        plural: 'Assets' },
  asset_type:   { singular: 'Asset Type',   plural: 'Asset Types' },
  form:         { singular: 'Form',         plural: 'Forms' },
  submission:   { singular: 'Submission',   plural: 'Submissions' },
  schedule:     { singular: 'Schedule',     plural: 'Schedules' },
  job_workflow: { singular: 'Job Workflow', plural: 'Job Workflows' },
  maintenance:  { singular: 'Maintenance task', plural: 'Maintenance tasks' },
};

// The full set of keys at runtime — useful for iterating the Settings
// UI without hardcoding the list there.
export const ALL_LABEL_KEYS: readonly LabelKey[] = Object.keys(DEFAULT_LABELS) as LabelKey[];

// Quick-start templates — one click in Settings → Terminology fills the
// fields for a vertical. Tenants can then tweak. Add new presets here
// rather than hardcoding strings into the UI.
export type LabelPresetKey = 'cleaning' | 'fleet' | 'aged_care' | 'childcare' | 'construction' | 'hospitality';

export const LABEL_PRESETS: Record<LabelPresetKey, { name: string; description: string; labels: Partial<Record<LabelKey, LabelForms>> }> = {
  cleaning: {
    name: 'Cleaning',
    description: 'Buildings → Zones → Checklists. Commercial cleaning, janitorial QA.',
    labels: {
      site:       { singular: 'Building',  plural: 'Buildings' },
      asset:      { singular: 'Zone',      plural: 'Zones' },
      asset_type: { singular: 'Zone type', plural: 'Zone types' },
      form:       { singular: 'Checklist', plural: 'Checklists' },
      submission: { singular: 'Check',     plural: 'Checks' },
      schedule:   { singular: 'Roster',    plural: 'Rosters' },
    },
  },
  fleet: {
    name: 'Fleet',
    description: 'Depots → Vehicles → Pre-trip inspections.',
    labels: {
      site:       { singular: 'Depot',         plural: 'Depots' },
      asset:      { singular: 'Vehicle',       plural: 'Vehicles' },
      asset_type: { singular: 'Vehicle class', plural: 'Vehicle classes' },
      form:       { singular: 'Inspection',    plural: 'Inspections' },
      submission: { singular: 'Trip log',      plural: 'Trip logs' },
      schedule:   { singular: 'Run sheet',     plural: 'Run sheets' },
    },
  },
  aged_care: {
    name: 'Aged care',
    description: 'Wings → Residents → Care plans. Non-clinical observations.',
    labels: {
      site:       { singular: 'Wing',          plural: 'Wings' },
      asset:      { singular: 'Resident',      plural: 'Residents' },
      asset_type: { singular: 'Care category', plural: 'Care categories' },
      form:       { singular: 'Care record',   plural: 'Care records' },
      submission: { singular: 'Observation',   plural: 'Observations' },
      schedule:   { singular: 'Care plan',     plural: 'Care plans' },
    },
  },
  childcare: {
    name: 'Childcare / ECE',
    description: 'Centres → Children → Daily logs.',
    labels: {
      site:       { singular: 'Centre',     plural: 'Centres' },
      asset:      { singular: 'Child',      plural: 'Children' },
      asset_type: { singular: 'Age group',  plural: 'Age groups' },
      form:       { singular: 'Daily log',  plural: 'Daily logs' },
      submission: { singular: 'Daily entry', plural: 'Daily entries' },
      schedule:   { singular: 'Routine',    plural: 'Routines' },
    },
  },
  construction: {
    name: 'Construction',
    description: 'Sites → Plant/equipment → Pre-start checks.',
    labels: {
      site:       { singular: 'Site',           plural: 'Sites' },
      asset:      { singular: 'Plant',          plural: 'Plant' },           // mass noun: same plural
      asset_type: { singular: 'Trade',          plural: 'Trades' },
      form:       { singular: 'Pre-start',      plural: 'Pre-starts' },
      submission: { singular: 'Check',          plural: 'Checks' },
      schedule:   { singular: 'Job card',       plural: 'Job cards' },
    },
  },
  hospitality: {
    name: 'Hospitality / Hotels',
    description: 'Properties → Rooms/equipment → Inspections.',
    labels: {
      site:       { singular: 'Property',   plural: 'Properties' },
      asset:      { singular: 'Room',       plural: 'Rooms' },
      asset_type: { singular: 'Room type',  plural: 'Room types' },
      form:       { singular: 'Inspection', plural: 'Inspections' },
      submission: { singular: 'Report',     plural: 'Reports' },
      schedule:   { singular: 'Roster',     plural: 'Rosters' },
    },
  },
};

// Helpers to derive secondary copy from the singular/plural pair —
// avoid exploding the configurable surface for things that follow a
// pattern. Tenants can still override behaviour by changing the
// underlying singular/plural; explicit verb-form overrides come later
// if a real customer asks.
export const labelCTA = {
  add: (singular: string) => `Add ${singular}`,
  create: (singular: string) => `Create ${singular}`,
  emptyTitle: (singular: string) => `Add your first ${singular}`,
  manage: (plural: string) => `Manage ${plural}`,
};
